import React, { useState, useEffect, useRef } from 'react';
import './LandingPage.css';
import ResultsDashboard from './ResultsDashboard';
import ResultsPreviewSection from './ResultsPreviewSection';
import ErrorModal, { errorTypeToModalType } from '../components/ErrorModal';
import HotelConfirmation from '../components/HotelConfirmation';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/dhihospitality-audit-review/30min?hide_event_type_details=1&hide_gdpr_banner=1';

// ============================================================================
// CONSTANTS
// ============================================================================

// ============================================================================
// EMAIL VALIDATION — mirrored from backend email_validation.py
// Kept in sync to give instant frontend feedback; backend is authoritative.
// ============================================================================

const CONSUMER_DOMAINS = [
  "gmail.com", "googlemail.com", "yahoo.com", "ymail.com", "rocketmail.com",
  "hotmail.com", "outlook.com", "live.com", "msn.com",
  "icloud.com", "me.com", "mac.com",
  "aol.com", "protonmail.com", "proton.me", "pm.me",
  "mail.com", "gmx.com", "gmx.net", "gmx.de",
  "zoho.com", "fastmail.com", "tutanota.com", "hushmail.com",
  "yandex.com", "yandex.ru", "mail.ru", "inbox.ru",
  "qq.com", "163.com", "126.com", "sina.com", "sohu.com",
  "naver.com", "daum.net", "rediffmail.com", "rediff.com",
];

const DISPOSABLE_DOMAINS = [
  "mailinator.com", "tempmail.com", "guerrillamail.com", "throwam.com",
  "yopmail.com", "sharklasers.com", "spam4.me", "trashmail.com",
  "dispostable.com", "maildrop.cc", "fakeinbox.com", "mytrashmail.com",
  "10minutemail.com", "10minutemail.net", "temp-mail.org", "temp-mail.io",
  "getnada.com", "nada.email", "mohmal.com", "moakt.com",
  "emailondeck.com", "tempinbox.com", "mintemail.com", "burnermail.io",
  "guerrillamailblock.com", "grr.la", "mailnesia.com", "mailcatch.com",
  "mailnull.com", "inbox.lv", "mytemp.email", "emailfake.com",
  "emltmp.com", "spamgourmet.com", "throwawaymail.com",
  "luxusmail.org", "dropmail.me", "anonmail.com",
];

const PLACEHOLDER_DOMAINS = [
  "test.com", "test.co", "test.net", "test.org", "test.io",
  "example.com", "example.org", "example.net",
  "sample.com", "demo.com",
  "abc.com", "xyz.com", "aaa.com", "bbb.com", "ccc.com",
  "foo.com", "bar.com", "baz.com",
  "email.com", "domain.com", "website.com",
  "company.com", "mycompany.com", "yourcompany.com",
  "test123.com", "test1.com", "test2.com",
  "shsh.com", "asdf.com", "qwerty.com", "qwe.com",
  "hotel.com", "fakecorp.com", "fakecompany.com",
  "dummy.com", "placeholder.com", "123.com",
];

const JUNK_LOCAL_PARTS = new Set([
  "test", "tests", "testing", "testuser",
  "test1", "test2", "test3", "testing1", "testing2",
  "demo", "demos", "demouser",
  "fake", "dummy", "sample",
  "admin", "administrator", "root",
  "noreply", "no-reply", "no.reply", "donotreply",
  "info", "contact", "hello", "help",
  "example", "user", "user1", "user2", "guest",
  "foo", "bar", "baz", "abc", "xyz",
  "qwerty", "asdf", "zxcv", "asdfgh", "qwertyuiop",
  "a", "aa", "aaa", "aaaa", "aaaaa",
  "123", "1234", "12345", "123456",
  "temp", "tmp", "anon", "anonymous",
]);

// Validates email matches our ICP (business email) pattern.
// Returns a user-facing error string, or null if valid.
function validateBusinessEmail(email) {
  if (!email) return 'Email is required';
  const lower = email.trim().toLowerCase();

  // Format
  if (!/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(lower)) {
    return 'Please enter a valid email address';
  }
  const [local, domain] = lower.split('@');
  if (!local || !domain) return 'Please enter a valid email address';

  // Length sanity
  if (local.length < 2) return 'Email local part is too short to be real';
  if (domain.length < 6) return 'This domain is too short — please use your work email';

  // Blocklists
  if (CONSUMER_DOMAINS.includes(domain)) return 'Please use your work email (not a consumer mailbox)';
  if (DISPOSABLE_DOMAINS.includes(domain)) return 'Disposable email addresses are not accepted';
  if (PLACEHOLDER_DOMAINS.includes(domain)) return 'Please enter your real work email address';

  // Test-pattern local parts
  if (JUNK_LOCAL_PARTS.has(local)) return 'Please enter your real work email (not a test or generic mailbox)';

  // Keyboard mash + repeated chars
  if (/(.)\1{3,}/.test(local)) return "This doesn't look like a real email — please enter your work email";
  const mashStarts = ['qwer', 'asdf', 'zxcv', '1234'];
  if (mashStarts.some(m => local.startsWith(m))) {
    return "This doesn't look like a real email — please enter your work email";
  }

  return null;
}

const TESTIMONIALS = [
  { initials: "GM", quote: "We thought our website was fine. The audit showed otherwise. There were gaps we had no idea existed, and fixing them made an immediate difference to how AI platforms were reading our property.", role: "General Manager", hotel: "Boutique beachfront resort — Koh Samui, Thailand" },
  { initials: "RD", quote: "Seeing the volume of AI searches we were missing every month was the wake-up call. Guests were actively looking for hotels like ours and we simply were not in the conversation.", role: "Revenue Director", hotel: "Independent city hotel — London, UK" },
  { initials: "GM", quote: "We had no idea how one-dimensional our digital presence looked to AI. Rooms were front and centre, but everything else the property offers barely registered. We were leaving a lot on the table.", role: "General Manager", hotel: "Overwater boutique resort — Maldives" },
  { initials: "CD", quote: "Running multiple properties, you need a consistent way to measure performance. This gave us a visibility score for each hotel in the group, so we could see exactly where to focus.", role: "Commercial Director", hotel: "Luxury hotel group — Dubai, UAE" },
  { initials: "GM", quote: "Guests were asking AI which hotel to use for their corporate events and our competitors kept coming up. We had the facilities, we just were not showing up. The audit told us why and what to do about it.", role: "General Manager", hotel: "Boutique business hotel — Singapore" },
  { initials: "AD", quote: "Across our Bangkok properties, AI kept pointing guests to other hotels. We assumed it was a brand awareness issue. It turned out to be something far more fixable, and the audit laid it out clearly.", role: "Area Director of Revenue Strategy", hotel: "Lifestyle hotel group — Bangkok, Thailand" },
];

// FAQ source of truth — single JSON file feeds:
//   1. The visible FAQ accordion in this component (below)
//   2. The static FAQPage JSON-LD schema in public/index.html
//   3. The non-JS crawler fallback inside the <noscript> block
// Sync from JSON → static HTML happens via scripts/sync-seo-faq.js
// (runs as `prebuild` so every deployment ships matching content).
import FAQ_ITEMS from '../data/faq.json';

const PLATFORM_LOGOS = [
  { name: "ChatGPT", url: "https://wsrv.nl/?url=cdn.prod.website-files.com%2F686e2126992edd7b040ec218%2F69ccedef9ce81893c1ef2c17_chatgpt-icon.webp&w=96&h=96&output=webp" },
  { name: "Gemini", url: "https://cdn.simpleicons.org/googlegemini/0A0F2E" },
  { name: "Perplexity", url: "https://cdn.simpleicons.org/perplexity/0A0F2E" },
  { name: "Claude", url: "https://cdn.simpleicons.org/claude/0A0F2E" },
];

const CURRENCIES = ["USD","EUR","GBP","AED","AUD","BHD","CAD","CHF","CNY","HKD","IDR","INR","JPY","KWD","MYR","NZD","QAR","SAR","SGD","THB","TRY","ZAR"];

// ============================================================================
// ICONS
// ============================================================================

const CheckIcon = () => (
  <svg viewBox="0 0 10 10" fill="none" width="10" height="10">
    <path d="M2 5.5L4 7.5L8 3" stroke="#2D4DE0" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClaudeSvg = () => (
  <svg width="40" height="40" viewBox="0 0 16 16" fill="#0A0F2E"><path d="m3.127 10.604 3.135-1.76.053-.153-.053-.085H6.11l-.525-.032-1.791-.048-1.554-.065-1.505-.08-.38-.081L0 7.832l.036-.234.32-.214.455.04 1.009.069 1.513.105 1.097.064 1.626.17h.259l.036-.105-.089-.065-.068-.064-1.566-1.062-1.695-1.121-.887-.646-.48-.327-.243-.306-.104-.67.435-.48.585.04.15.04.593.456 1.267.981 1.654 1.218.242.202.097-.068.012-.049-.109-.181-.9-1.626-.96-1.655-.428-.686-.113-.411a2 2 0 0 1-.068-.484l.496-.674L4.446 0l.662.089.279.242.411.94.666 1.578.72 1.533.505 1.09.157.38.085-.016.048-.08V5.62l.032-.736.049-1.603.064-1.19.081-1.351.104-.42L8.35 0l.553.089.214.35-.04.457-.07 1.009-.104 1.512-.065 1.097-.169 1.627v.258l.104.036.065-.088.065-.068 1.062-1.566 1.12-1.695.647-.888.326-.48.307-.242.67-.105.48.436-.04.584-.04.15-.457.594-.98 1.267-1.218 1.653-.203.242.068.097.049.013.181-.11 1.626-.899 1.654-.96.687-.428.41-.113a2 2 0 0 1 .485-.068l.673.496.048.67-.088.28-.242.278-.94.412-1.578.665-1.534.72-1.089.506-.38.156-.017.085.081.049h.116l.737.032 1.602.048 1.19.065 1.352.08.42.105.38.235-.09.552-.348.215-.458-.041-1.008-.069-1.513-.105-1.097-.064-1.627-.17h-.258l-.036.105.088.064.065.065 1.565 1.062 1.695 1.12.888.647.48.326.243.307.104.67-.436.48-.584-.04-.15-.04-.593-.457-1.268-.98-1.653-1.218-.242-.203-.097.068-.013.049.11.18.899 1.627.96 1.654.428.687.113.41c.042.156.065.316.068.485l-.496.673-.67.049-.28-.089-.278-.242-.411-.94-.666-1.577-.72-1.534-.505-1.09-.157-.38-.085.017-.048.08v.116l-.032.736-.049 1.603-.065 1.19-.08 1.351-.105.42-.234.381-.553-.089-.214-.35.04-.457.07-1.009.104-1.512.064-1.097.17-1.627v-.259l-.105-.036-.064.089-.065.068-1.062 1.566-1.12 1.695-.647.887-.327.48-.306.243-.67.104-.48-.435.04-.585.04-.15.456-.593.981-1.267 1.218-1.654.202-.242-.068-.097-.048-.012-.181.109-1.627.9-1.653.96-.687.427-.41.113a2 2 0 0 1-.485.068l-.673-.496-.048-.67.089-.279z"/></svg>
);

// ============================================================================
// STAR BUTTON
// ============================================================================

const StarButton = ({ label, variant = "primary", onClick, disabled, loading, className = "" }) => {
  const isPrimary = variant === "primary";
  return (
    <button
      className={`form-cta ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
      data-testid="star-button"
    >
      {loading ? <><div className="spinner"></div> Running your audit...</> : <><div className="cta-pulse"></div> {label}</>}
    </button>
  );
};

// ============================================================================
// NAV
// ============================================================================

const Nav = () => (
  <nav className="nav" data-testid="nav">
    <a href="/" className="nav-logo">
      <img src="https://cdn.prod.website-files.com/686e2126992edd7b040ec218/69b4f893406935517896aa07_logo.avif" alt="dhi Hospitality" style={{height:32,width:'auto'}} />
    </a>
    <div className="nav-status">
      <div className="nav-dot"></div>
      <span>Live AI Data</span>
    </div>
  </nav>
);

// ============================================================================
// HERO SECTION (with form)
// ============================================================================

const HeroSection = ({ formRef, onSubmit }) => {
  const [formData, setFormData] = useState({
    url: '', email: '', rooms: '', currency: 'USD', adr: '', los: '',
  });
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleUrlBlur = (e) => {
    let val = e.target.value.trim();
    if (val && !val.startsWith('http')) {
      setFormData(prev => ({ ...prev, url: 'https://' + val }));
    }
  };

  const validateForm = () => {
    const e = {};
    if (!formData.url) {
      e.url = 'URL is required';
    } else if (!formData.url.includes('.')) {
      e.url = 'Please enter a valid URL';
    } else {
      const hostname = formData.url
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .toLowerCase();
      const blockedDomains = [
        'example.com', 'example.org', 'test.com',
        'booking.com', 'expedia.com', 'hotels.com',
        'tripadvisor.com', 'agoda.com', 'airbnb.com',
      ];
      if (blockedDomains.includes(hostname)) {
        e.url = 'Please enter your hotel\'s own website URL, not an OTA or placeholder domain.';
      }
    }
    const emailErr = validateBusinessEmail(formData.email);
    if (emailErr) e.email = emailErr;
    if (!formData.rooms) e.rooms = 'Required';
    else if (parseInt(formData.rooms) < 1) e.rooms = 'Min 1';
    if (!formData.adr) e.adr = 'Required';
    else if (parseFloat(formData.adr) < 1) e.adr = 'Min 1';
    else if (parseFloat(formData.adr) > 500000) e.adr = 'Please check — this seems high';
    if (!formData.los) e.los = 'Required';
    else if (parseFloat(formData.los) < 0.5) e.los = 'Min 0.5';
    if (!termsAccepted) e.terms = 'You must accept the terms';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setSubmitting(true);
      // GTM / GA4 event — fired when a user with a valid form submits the audit request.
      // Populates dataLayer for GTM to forward to GA4, Meta Pixel, LinkedIn Insight, etc.
      try {
        const urlDomain = formData.url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'audit_started',
          audit_url_domain: urlDomain,
          audit_rooms: parseInt(formData.rooms) || 0,
          audit_currency: formData.currency,
          audit_adr: parseFloat(formData.adr) || 0,
          audit_los: parseFloat(formData.los) || 0,
          email_domain: (formData.email.split('@')[1] || '').toLowerCase(),
        });
        // PostHog capture (best-effort, fire-and-forget)
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture('audit_started', {
            url_domain: urlDomain,
            rooms: parseInt(formData.rooms) || 0,
            currency: formData.currency,
            adr: parseFloat(formData.adr) || 0,
            los: parseFloat(formData.los) || 0,
            email_domain: (formData.email.split('@')[1] || '').toLowerCase(),
          });
        }
      } catch (_err) { /* analytics failure must never block audit */ }
      onSubmit(formData);
    }
  };

  return (
    <section style={{background:'#fff'}} ref={formRef} data-testid="hero-section">
      <div className="hero">
        <div className="left">
          <div className="eyebrow">
            <div className="eyebrow-dot"></div>
            <span className="eyebrow-text">Hotel AI Visibility Audit</span>
          </div>
          <h1>Is your hotel <span className="h1-blue">invisible to AI?</span></h1>
          <p className="sub">AI search has changed how guests discover and book hotels. Most properties don't know where they stand or what it's costing them. This free audit gives you the clarity to act in less than 60 seconds.</p>
          <div className="bullets">
            {["Find out if ChatGPT, Gemini and Perplexity are recommending your hotel — or your competitors",
              "Quantify the direct booking revenue your hotel is losing to AI-driven OTA referrals",
              "Get a prioritised fix plan ranked by revenue impact, in the order that matters most to your bottom line"].map((t,i) => (
              <div className="bullet" key={i}>
                <div className="bullet-icon"><CheckIcon /></div>
                <span className="bullet-text">{t}</span>
              </div>
            ))}
          </div>
          <div className="powered">
            <span className="powered-label">Powered by</span>
            <div className="powered-logos">
              <div className="llm-chip"><img src={PLATFORM_LOGOS[0].url} style={{width:40,height:40,objectFit:'contain'}} alt="ChatGPT" /><span className="llm-chip-name">ChatGPT</span></div>
              <div className="llm-chip"><img src={PLATFORM_LOGOS[1].url} style={{width:40,height:40,objectFit:'contain'}} alt="Gemini" /><span className="llm-chip-name">Gemini</span></div>
              <div className="llm-chip"><img src={PLATFORM_LOGOS[2].url} style={{width:40,height:40,objectFit:'contain'}} alt="Perplexity" /><span className="llm-chip-name">Perplexity</span></div>
              <div className="llm-chip"><ClaudeSvg /><span className="llm-chip-name">Claude</span></div>
            </div>
          </div>
        </div>

        <div className="form-card" data-testid="form-card">
          <div className="form-title">Request Your Audit</div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Hotel Website URL</label>
              <input type="text" name="url" placeholder="https://yourhotel.com" value={formData.url} onChange={handleChange} onBlur={handleUrlBlur} className={`form-input ${errors.url ? 'error' : ''}`} data-testid="input-url" />
              {errors.url && <span className="form-error">{errors.url}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input type="email" name="email" placeholder="you@yourhotel.com" value={formData.email} onChange={handleChange} className={`form-input ${errors.email ? 'error' : ''}`} data-testid="input-email" />
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Room Count</label>
                <input type="number" name="rooms" placeholder="e.g. 150" min="1" value={formData.rooms} onChange={handleChange} className={`form-input ${errors.rooms ? 'error' : ''}`} data-testid="input-rooms" />
                {errors.rooms && <span className="form-error">{errors.rooms}</span>}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="currency">Currency</label>
                <select id="currency" name="currency" aria-label="Currency" value={formData.currency} onChange={handleChange} className="form-input" data-testid="select-currency">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">ADR</label>
                <input type="number" name="adr" placeholder="e.g. 250" min="1" value={formData.adr} onChange={handleChange} className={`form-input ${errors.adr ? 'error' : ''}`} data-testid="input-adr" />
                {errors.adr && <span className="form-error">{errors.adr}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Avg. LOS</label>
                <input type="number" name="los" placeholder="e.g. 2" min="0.5" step="0.1" value={formData.los} onChange={handleChange} className={`form-input ${errors.los ? 'error' : ''}`} data-testid="input-los" />
                {errors.los && <span className="form-error">{errors.los}</span>}
              </div>
            </div>
            <div className="form-terms">
              <input type="checkbox" id="terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} data-testid="checkbox-terms" />
              <label htmlFor="terms">I agree to the <a href="https://dhihospitality.com/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="https://dhihospitality.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></label>
            </div>
            {errors.terms && <span className="form-error">{errors.terms}</span>}
            <StarButton label="Check My AI Visibility Score" variant="primary" loading={submitting} />
            <p className="form-note">Live AI platform data. No login required.</p>
          </form>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// LOADING CARD
// ============================================================================

const FEED_LINES = [
  { type: "system", text: "Initializing AI visibility audit protocol..." },
  { type: "data",   text: "Resolving domain and following redirects..." },
  { type: "data",   text: "Fetching hotel website content..." },
  { type: "found",  text: "Website successfully loaded" },
  { type: "data",   text: "Parsing page structure and metadata..." },
  { type: "data",   text: "Extracting schema.org markup..." },
  { type: "data",   text: "Reading Open Graph tags..." },
  { type: "data",   text: "Scanning page title and meta description..." },
  { type: "found",  text: "Hotel profile extracted" },
  { type: "system", text: "Building guest query set for AI platforms..." },
  { type: "data",   text: "Generating brand recognition queries..." },
  { type: "data",   text: "Generating competitive positioning queries..." },
  { type: "data",   text: "Generating location-based discovery queries..." },
  { type: "data",   text: "Generating amenity-specific queries..." },
  { type: "data",   text: "Generating occasion and use-case queries..." },
  { type: "data",   text: "Generating trust and reputation queries..." },
  { type: "found",  text: "24-query test set ready" },
  { type: "system", text: "Probing ChatGPT (GPT-4o)..." },
  { type: "data",   text: "Sending query 1/6 to ChatGPT..." },
  { type: "data",   text: "Sending query 2/6 to ChatGPT..." },
  { type: "data",   text: "Sending query 3/6 to ChatGPT..." },
  { type: "data",   text: "Sending query 4/6 to ChatGPT — competitor detected" },
  { type: "data",   text: "Sending query 5/6 to ChatGPT..." },
  { type: "data",   text: "Sending query 6/6 to ChatGPT..." },
  { type: "found",  text: "ChatGPT responses captured" },
  { type: "system", text: "Probing Google Gemini..." },
  { type: "data",   text: "Sending query 1/6 to Gemini..." },
  { type: "data",   text: "Sending query 2/6 to Gemini..." },
  { type: "data",   text: "Sending query 3/6 to Gemini — hotel not mentioned" },
  { type: "data",   text: "Sending query 4/6 to Gemini..." },
  { type: "data",   text: "Sending query 5/6 to Gemini..." },
  { type: "data",   text: "Sending query 6/6 to Gemini..." },
  { type: "found",  text: "Gemini responses captured" },
  { type: "system", text: "Probing Perplexity AI..." },
  { type: "data",   text: "Sending query 1/6 to Perplexity..." },
  { type: "data",   text: "Sending query 2/6 to Perplexity..." },
  { type: "data",   text: "Sending query 3/6 to Perplexity..." },
  { type: "data",   text: "Sending query 4/6 to Perplexity — OTA cited instead" },
  { type: "data",   text: "Sending query 5/6 to Perplexity..." },
  { type: "data",   text: "Sending query 6/6 to Perplexity..." },
  { type: "found",  text: "Perplexity responses captured" },
  { type: "system", text: "Probing Claude (Anthropic)..." },
  { type: "data",   text: "Sending query 1/6 to Claude..." },
  { type: "data",   text: "Sending query 2/6 to Claude..." },
  { type: "data",   text: "Sending query 3/6 to Claude..." },
  { type: "data",   text: "Sending query 4/6 to Claude..." },
  { type: "data",   text: "Sending query 5/6 to Claude..." },
  { type: "data",   text: "Sending query 6/6 to Claude..." },
  { type: "found",  text: "All 24 AI responses captured" },
  { type: "system", text: "Analysing citation patterns across platforms..." },
  { type: "data",   text: "Extracting competitor hotel mentions..." },
  { type: "data",   text: "Mapping which queries you were cited in..." },
  { type: "data",   text: "Identifying citation reasons and gaps..." },
  { type: "system", text: "Scoring Brand Recognition (22%)..." },
  { type: "system", text: "Scoring Competitive Share (18%)..." },
  { type: "system", text: "Scoring Content Depth (22%)..." },
  { type: "system", text: "Scoring Technical Readiness (18%)..." },
  { type: "system", text: "Scoring Trust Signals (20%)..." },
  { type: "found",  text: "All 5 parameters scored" },
  { type: "system", text: "Calculating blended AI visibility score..." },
  { type: "system", text: "Estimating annual direct revenue at risk..." },
  { type: "system", text: "Ranking actions by revenue recovery impact..." },
  { type: "system", text: "Building your prioritised action plan..." },
  { type: "found",  text: "Audit complete — compiling your report..." },
];

const LoadingCard = ({ hotelName }) => {
  const [progress, setProgress] = useState(0);
  const [feedLines, setFeedLines] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const feedRef = useRef(null);
  const lineIndexRef = useRef(0);

  // LoadingCard runs until the parent unmounts it (LandingPage transitions
  // pageState off 'loading'). All three timers below are cleaned up by their
  // effect return functions on unmount. Previously a dead `isComplete` state
  // (declared with no setter, always false) gated each effect — branches that
  // could never fire. Removed in PR #29 — behaviour is identical.
  useEffect(() => {
    const progTimer = setInterval(() => {
      setProgress(prev => {
        if (prev < 30) return prev + 0.8;
        if (prev < 60) return prev + 0.5;
        if (prev < 80) return prev + 0.3;
        if (prev < 92) return prev + 0.15;
        if (prev < 95) return prev + 0.05;
        return prev;
      });
    }, 400);
    return () => clearInterval(progTimer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Terminal feed — new line every 2.0–2.8s, cycles back to line 50 after all 64 lines
  useEffect(() => {
    const addLine = () => {
      if (lineIndexRef.current >= FEED_LINES.length) {
        lineIndexRef.current = 50;
      }
      const line = FEED_LINES[lineIndexRef.current];
      lineIndexRef.current++;
      setFeedLines(prev => [...prev, { ...line, id: Date.now() }].slice(-8));
    };
    addLine();
    const timerRef = { current: null };
    const schedule = () => {
      const delay = 2000 + Math.random() * 800;
      return setTimeout(() => { addLine(); timerRef.current = schedule(); }, delay);
    };
    timerRef.current = schedule();
    return () => clearTimeout(timerRef.current);
  }, []);

  // Auto-scroll terminal
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [feedLines]);

  // Honest tiered messaging — first three rungs keep the user oriented through
  // a typical audit; the 240s tier warns them we're close to the 5-minute hard
  // timeout so a fail isn't a surprise after they've been watching a spinner.
  const footerText = elapsed < 90
    ? 'This typically takes around 90 seconds'
    : elapsed < 150
    ? 'Taking a little longer than usual — almost there...'
    : elapsed < 240
    ? 'This is a thorough audit — wrapping up now...'
    : "Still working — if this exceeds 5 minutes we'll surface an error so you can retry.";

  return (
    <div style={{display:'flex',flexDirection:'column',minHeight:'60vh',background:'#F8F9FF'}} data-testid="loading-card">
      <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',padding:'64px 16px'}}>
        <div style={{width:'100%',maxWidth:480,textAlign:'center'}}>
          <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.18em',textTransform:'uppercase',color:'rgba(10,10,26,0.65)',marginBottom:12}}>ANALYSING YOUR PROPERTY</p>
          <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:'clamp(24px, 5vw, 36px)',fontWeight:800,color:'#0A0A1A',letterSpacing:'-0.02em',marginBottom:8}}>{hotelName || 'Your Hotel'}</h2>
          <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:14,color:'rgba(10,10,26,0.65)',marginBottom:48}}>Running queries across ChatGPT, Gemini, Perplexity and Claude</p>
          <div style={{marginBottom:32}}>
            <div style={{display:'flex',justifyContent:'space-between',fontFamily:"'DM Sans',sans-serif",fontSize:12,color:'rgba(10,10,26,0.62)',marginBottom:10}}>
              <span>Progress</span><span>{Math.round(progress)}%</span>
            </div>
            <div style={{height:6,background:'rgba(10,10,26,0.08)',borderRadius:99,overflow:'hidden'}}>
              <div style={{height:'100%',width:`${progress}%`,background:'linear-gradient(90deg, #2D4DE0, #4A6FE3)',borderRadius:99,transition:'width 0.3s ease-out'}} />
            </div>
          </div>
          <div ref={feedRef} style={{background:'#0A0A1A',borderRadius:12,padding:'18px 20px',maxHeight:200,overflowY:'auto',textAlign:'left'}} data-testid="intelligence-feed">
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              {feedLines.map((line, i) => (
                <div key={line.id || i} style={{display:'flex',alignItems:'flex-start',gap:8,fontFamily:"'Courier New',monospace",fontSize:12,lineHeight:1.75,animation:'fadeIn 0.3s ease forwards'}}>
                  <span style={{flexShrink:0,color:line.type==='found'?'#1A7A52':line.type==='system'?'#2D4DE0':'rgba(255,255,255,0.3)'}}>
                    {line.type==='found'?'[OK] ':line.type==='system'?'[SYS]':'[...]'}
                  </span>
                  <span style={{color:line.type==='found'?'rgba(255,255,255,0.85)':line.type==='system'?'rgba(255,255,255,0.55)':'rgba(255,255,255,0.45)'}}>{line.text}</span>
                </div>
              ))}
              <div style={{display:'flex',alignItems:'center',gap:8,fontFamily:"'Courier New',monospace",fontSize:12}}>
                <span style={{color:'rgba(255,255,255,0.3)'}}>{'[...]'}</span>
                <span style={{color:'rgba(255,255,255,0.3)',animation:'blink 1s step-end infinite'}}>_</span>
              </div>
            </div>
          </div>
          <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:'rgba(10,10,26,0.62)',marginTop:24}}>{footerText}</p>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// ERROR HANDLING  (modal UI is in components/ErrorModal.js — this section
// only holds the API-response parser that maps backend tags to errorType)
// ============================================================================

// Shared error parser — handles both backend response shapes:
//   1. Structured HTTPException: { detail: { error, message, error_type } }
//   2. Pydantic validator error:  { detail: [{ type, loc, msg: "Value error, [TAG] ..." }] }
// Extracts [TAG] prefix into errorType either way. Returns { errorType, errorMsg }.
const parseApiError = (data) => {
  const detail = data?.detail ?? data ?? {};

  // Shape 1 — explicit HTTPException raised by server.py
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const msg = detail.message || detail.detail || '';
    const explicitType = (detail.error_type || detail.reason || detail.error || '').toLowerCase();
    // [TAG] prefix inside message takes precedence (background-task pipeline errors)
    const tagMatch = String(msg).match(/^\s*\[([A-Z_]+)\]\s*(.*)$/);
    if (tagMatch) return { errorType: tagMatch[1].toLowerCase(), errorMsg: tagMatch[2] };
    return { errorType: explicitType, errorMsg: msg };
  }

  // Shape 2 — Pydantic validator error (detail is an array)
  if (Array.isArray(detail) && detail.length > 0) {
    for (const item of detail) {
      const raw = String(item?.msg || '');
      // Pydantic wraps as "Value error, [TAG] message"
      const m = raw.match(/\[([A-Z_]+)\]\s*(.*)$/);
      if (m) return { errorType: m[1].toLowerCase(), errorMsg: m[2].trim() };
    }
    return { errorType: 'validation_error', errorMsg: detail[0]?.msg || 'Invalid input' };
  }

  // Shape 3 — bare string or unknown
  const fallback = typeof data === 'string' ? data : (data?.message || '');
  return { errorType: '', errorMsg: fallback };
};

// ============================================================================
// HOW IT WORKS
// ============================================================================

const HowItWorksSection = ({ scrollToForm }) => (
  <section className="how-section" style={{background:'var(--bg)',borderTop:'1px solid rgba(10,10,26,0.07)'}}>
    <div className="fold-inner">
      <p className="fold-eyebrow-dark">What the audit delivers</p>
      <h2 className="fold-h2-dark">What does ChatGPT say when a guest asks for the best hotel in your market?</h2>
      <p style={{fontSize:14,fontWeight:500,color:'rgba(10,10,26,0.65)',lineHeight:1.7,margin:'0 0 32px'}}>Most hotels are losing direct bookings to AI search and don't know it. This is what the audit tells you.</p>
      <div className="how-grid">
        <div style={{background:'#fff',border:'1px solid rgba(10,10,26,0.07)',borderRadius:14,padding:'28px 26px'}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:700,color:'#2D4DE0',marginBottom:16}}>01</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:'#0A0F2E',marginBottom:10,lineHeight:1.25}}>Who is AI recommending instead of you?</div>
          <p style={{fontSize:14,color:'rgba(10,10,26,0.68)',lineHeight:1.7,margin:0}}>We run real guest queries across ChatGPT, Gemini, Perplexity and Claude. You see which competitors appear — by name, by query, by platform — and the exact reason you were not cited.</p>
        </div>
        <div style={{background:'#fff',border:'1px solid rgba(10,10,26,0.07)',borderRadius:14,padding:'28px 26px'}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:700,color:'#2D4DE0',marginBottom:16}}>02</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:'#0A0F2E',marginBottom:10,lineHeight:1.25}}>What is that costing you in direct revenue?</div>
          <p style={{fontSize:14,color:'rgba(10,10,26,0.68)',lineHeight:1.7,margin:0}}>Every visibility gap is translated into a revenue figure — in your currency, using your ADR and occupancy. Not a benchmark. Your number, for your property.</p>
        </div>
        <div style={{background:'#0A0A1A',borderRadius:14,padding:'28px 26px'}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:700,color:'rgba(255,255,255,0.35)',marginBottom:16}}>03</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:'#fff',marginBottom:10,lineHeight:1.25}}>What do you fix first?</div>
          <p style={{fontSize:14,color:'rgba(255,255,255,0.45)',lineHeight:1.7,margin:'0 0 20px'}}>A prioritised action plan ranked by revenue recovery, not technical complexity. Written for your GM or agency to act on immediately.</p>
          <button onClick={scrollToForm} style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:13,fontWeight:600,color:'#fff',background:'none',border:'none',borderBottom:'1px solid rgba(255,255,255,0.3)',paddingBottom:2,cursor:'pointer'}}>
            Get your action plan
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </div>
    </div>
  </section>
);

// ============================================================================
// DARK STATS SECTION
// ============================================================================

const DarkStatsSection = () => (
  <section className="stats-section" style={{background:'var(--bg)',borderTop:'1px solid rgba(10,10,26,0.07)'}}>
    <div className="fold-inner stats-split">
      <div>
        <p className="fold-eyebrow-dark" style={{marginBottom:12}}>The direct booking gap</p>
        <h2 className="fold-h2-dark" style={{marginBottom:16}}>AI search is the new front desk. Most hotels aren't in the room.</h2>
        <p className="fold-sub-dark" style={{marginBottom:0}}>ChatGPT, Gemini and Perplexity now influence hotel decisions at the same rate as Google search did five years ago. The properties that show up are not necessarily the best ones. They are the most machine-readable ones.</p>
      </div>
      <div className="stats-grid">
        {[
          {n:'68%', d:'of hotel guests now use AI to research and shortlist properties before booking'},
          {n:'3.1x', d:"higher direct booking rate for hotels that appear in AI responses vs those that don't"},
          {n:'31',  d:'Average AI visibility score across independent luxury properties we have audited', blue:true},
          {n:'60s', d:'Time it takes to submit your hotel for a full AI visibility audit across all four platforms'},
        ].map((s,i) => (
          <div key={i} style={{background:'#0A0A1A',border:'1px solid rgba(255,255,255,0.06)',borderRadius:14,padding:'24px 20px'}}>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:36,fontWeight:800,color:s.blue?'#2D4DE0':'#fff',marginBottom:6}}>{s.n}</div>
            <div style={{fontSize:13,color:'rgba(255,255,255,0.45)',lineHeight:1.6}}>{s.d}</div>
          </div>
        ))}
      </div>
    </div>
    <p style={{fontSize:11,color:'rgba(10,10,26,0.62)',textAlign:'center',maxWidth:1060,margin:'24px auto 0'}}>Industry estimates based on dhi Hospitality audit data and published AI search research.</p>
  </section>
);

// ============================================================================
// TESTIMONIALS
// ============================================================================

const TestimonialsSection = () => {
  const [idx, setIdx] = useState(0);
  const getMax = () => TESTIMONIALS.length - 3;

  const slide = (dir) => setIdx(prev => Math.max(0, Math.min(prev + dir, getMax())));

  return (
    <section className="testimonials-section" data-testid="testimonials">
      <div className="fold-inner">
        <span className="fold-eyebrow-dark" style={{display:'block',textAlign:'center'}}>Testimonials</span>
        <h2 className="fold-h2-dark" style={{textAlign:'center',marginBottom:8}}>Hoteliers who have seen their AI score</h2>
        <p className="fold-sub-dark" style={{textAlign:'center',margin:'0 auto 48px',maxWidth:560}}>From independent luxury properties and hotel groups across Asia, Europe, the Middle East and the Americas.</p>
        <div style={{position:'relative',padding:'0 28px'}}>
          <button onClick={() => slide(-1)} disabled={idx===0} aria-label="Previous testimonials" type="button" style={{position:'absolute',left:0,top:'40%',transform:'translateY(-50%)',zIndex:2,width:44,height:44,borderRadius:'50%',background:'#fff',border:'1px solid rgba(10,10,26,0.12)',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',opacity:idx===0?0.4:1}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="#1C2470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div style={{overflow:'hidden'}}>
            <div style={{display:'flex',gap:20,transition:'transform 0.4s cubic-bezier(0.4,0,0.2,1)',transform:`translateX(-${idx*(Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 80 : 320)+20)}px)`}}>
              {TESTIMONIALS.map((t,i) => (
                <div key={i} style={{flex:'0 0 min(320px, calc(100vw - 80px))',minWidth:'min(320px, calc(100vw - 80px))',borderRadius:16,overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.06)',border:'1px solid rgba(10,10,26,0.08)'}}>
                  <div style={{background:'#F5F6FA',display:'flex',alignItems:'center',justifyContent:'center',height:110}}>
                    <div style={{width:56,height:56,borderRadius:'50%',background:'#EEF1FD',border:'2px solid rgba(45,77,224,0.18)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Outfit',sans-serif",fontWeight:800,fontSize:18,color:'#2D4DE0'}}>{t.initials}</div>
                  </div>
                  <div style={{background:'#fff',padding:'16px 18px 20px'}}>
                    <p style={{fontSize:13,lineHeight:1.7,color:'rgba(10,10,26,0.65)',marginBottom:14}}>"{t.quote}"</p>
                    <div style={{fontSize:13,fontWeight:700,color:'#0A0A1A',marginBottom:3}}>{t.role}</div>
                    <div style={{fontSize:11,fontWeight:500,color:'#2D4DE0'}}>{t.hotel}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => slide(1)} disabled={idx>=getMax()} aria-label="Next testimonials" type="button" style={{position:'absolute',right:0,top:'40%',transform:'translateY(-50%)',zIndex:2,width:44,height:44,borderRadius:'50%',background:'#fff',border:'1px solid rgba(10,10,26,0.12)',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',opacity:idx>=getMax()?0.4:1}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="#1C2470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
        <div style={{display:'flex',justifyContent:'center',gap:6,marginTop:24}}>
          {Array.from({length:getMax()+1}).map((_,i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Go to testimonial page ${i+1}`} aria-current={i===idx?'true':undefined} type="button" style={{width:i===idx?32:24,height:24,border:'none',background:'transparent',cursor:'pointer',padding:0,position:'relative',display:'inline-flex',alignItems:'center',justifyContent:'center',margin:'0 2px'}}><span aria-hidden="true" style={{display:'block',width:i===idx?20:8,height:8,borderRadius:4,background:i===idx?'var(--blue)':'rgba(10,10,26,0.3)',transition:'all 0.3s'}}/></button>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FAQ
// ============================================================================

const FaqSection = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="faq-section" data-testid="faq">
      <div className="fold-inner">
        <div style={{textAlign:'center',marginBottom:48}}>
          <span className="fold-eyebrow-dark">Got questions</span>
          <h2 className="fold-h2-dark" style={{marginBottom:8}}>Frequently asked questions about hotel AI visibility</h2>
          <p className="fold-sub-dark" style={{margin:'0 auto'}}>Everything you need to know before running your audit.</p>
        </div>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} style={{borderBottom:'1px solid var(--border)',...(i===0?{borderTop:'1px solid var(--border)'}:{})}}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 0',cursor:'pointer',width:'100%',background:'none',border:'none',gap:16,textAlign:'left'}}>
                <span className="fq-text">{item.q}</span>
                <span className="fq-icon" style={{transform: open === i ? 'rotate(45deg)' : 'none'}}>+</span>
              </button>
              {open === i && <div className="fq-answer">{item.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA STRIP
// ============================================================================

const CtaStrip = ({ headline, sub, btnLabel, scrollToForm }) => (
  <div className="cta-strip">
    <div>
      <div className="cta-strip-text">{headline}</div>
      <div className="cta-strip-sub">{sub}</div>
    </div>
    <button className="cta-btn" onClick={scrollToForm}>{btnLabel}</button>
  </div>
);

// ============================================================================
// FOOTER
// ============================================================================

const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="footer-grid">
      <div>
        <div style={{marginBottom:16}}>
          <img src="https://wsrv.nl/?url=cdn.prod.website-files.com%2F686e2126992edd7b040ec218%2F69b4f893406935517896aa07_logo.avif&w=128&h=108&output=webp" alt="dhi Hospitality" width="64" height="54" loading="eager" decoding="async" style={{height:32,width:'auto'}} />
        </div>
        <p className="footer-desc">AI-powered hotel visibility audits for brands serious about direct revenue.</p>
        <div style={{display:'flex',gap:8,marginBottom:16}}>
          <a href="https://www.linkedin.com/company/dhi-hospitality/" target="_blank" rel="noopener noreferrer" aria-label="dhi Hospitality on LinkedIn" title="LinkedIn" style={{width:34,height:34,borderRadius:8,background:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.6)"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
          </a>
          <a href="https://www.instagram.com/dhihospitality/" target="_blank" rel="noopener noreferrer" aria-label="dhi Hospitality on Instagram" title="Instagram" style={{width:34,height:34,borderRadius:8,background:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          </a>
          <a href="https://www.facebook.com/dhihospitality/" target="_blank" rel="noopener noreferrer" aria-label="dhi Hospitality on Facebook" title="Facebook" style={{width:34,height:34,borderRadius:8,background:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.6)"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
          </a>
          <a href="mailto:hello@dhihospitality.com" aria-label="Email dhi Hospitality" title="Email" style={{width:34,height:34,borderRadius:8,background:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',textDecoration:'none'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          </a>
        </div>
        <span className="footer-copy">&copy; 2026 dhi Hospitality. All rights reserved.</span>
      </div>
      <div className="footer-col">
        <span className="footer-col-header">Solutions</span>
        <a href="https://audit.dhihospitality.com/" target="_blank" rel="noopener noreferrer">AI Visibility Audit</a>
        <a href="https://audit.dhihospitality.com/" target="_blank" rel="noopener noreferrer">Hotel Digital Audit</a>
        <a href="https://dhihospitality.com/commercial-solutions" target="_blank" rel="noopener noreferrer">Commercial Solutions</a>
        <a href="https://dhihospitality.com/management-solutions" target="_blank" rel="noopener noreferrer">Management Solutions</a>
        <a href="https://dhihospitality.com/revenue-solutions" target="_blank" rel="noopener noreferrer">Revenue Solutions</a>
      </div>
      <div className="footer-col">
        <span className="footer-col-header">Company</span>
        <a href="https://dhihospitality.com/" target="_blank" rel="noopener noreferrer">Home</a>
        <a href="https://dhihospitality.com/team" target="_blank" rel="noopener noreferrer">Our Team</a>
        <a href="https://dhihospitality.com/blogs" target="_blank" rel="noopener noreferrer">Blog</a>
        <a href="https://dhihospitality.com/contact-us" target="_blank" rel="noopener noreferrer">Contact Us</a>
      </div>
      <div className="footer-col">
        <span className="footer-col-header">Powered by</span>
        <a href="https://openai.com" target="_blank" rel="noopener noreferrer">ChatGPT</a>
        <a href="https://gemini.google.com" target="_blank" rel="noopener noreferrer">Gemini</a>
        <a href="https://www.perplexity.ai" target="_blank" rel="noopener noreferrer">Perplexity</a>
        <a href="https://www.anthropic.com" target="_blank" rel="noopener noreferrer">Claude</a>
        <a href="https://developers.google.com/speed/pagespeed/insights/" target="_blank" rel="noopener noreferrer">Google APIs</a>
        <a href="https://dataforseo.com" target="_blank" rel="noopener noreferrer">DataForSEO</a>
      </div>
    </div>
    <div className="footer-bottom">
      <span>&copy; 2026 dhi Hospitality. All rights reserved.</span>
      <span><a href="https://dhihospitality.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{color:'rgba(255,255,255,0.35)',textDecoration:'none'}}>Privacy</a> &middot; <a href="https://dhihospitality.com/terms" target="_blank" rel="noopener noreferrer" style={{color:'rgba(255,255,255,0.35)',textDecoration:'none'}}>Terms</a></span>
    </div>
  </footer>
);

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================

const LandingPage = () => {
  const formRef = useRef(null);
  const [pageState, setPageState] = useState('idle'); // idle | extracting | confirming | loading | complete | error
  const [reportData, setReportData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [errorType, setErrorType] = useState(''); // backend-provided error_type for targeted modal copy
  const [hotelName, setHotelName] = useState('');
  const [auditEmail, setAuditEmail] = useState('');
  // SOFT_ENGAGEMENT — fires once when a repeat-auditor (3+ audits from same
  // email) hits a completion. Session-storage gates so we don't nag them.
  const [showSoftEngagement, setShowSoftEngagement] = useState(false);
  const [auditCount, setAuditCount] = useState(0);
  const [extractedData, setExtractedData] = useState(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFormSubmit = async (formData) => {
    const urlDomain = formData.url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    setHotelName(urlDomain);
    setAuditEmail(formData.email);
    setPageState('extracting');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      const resp = await fetch(`${API_URL}/api/extract-hotel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: formData.url }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        const { errorType: et, errorMsg: em } = parseApiError(data);
        setErrorType(et);
        setErrorMsg(em || 'Could not reach this website. Please check the URL.');
        setPageState('error');
        return;
      }

      setExtractedData({ ...data, formData });
      setHotelName(data.hotelName || urlDomain);
      setPageState('confirming');

    } catch (err) {
      setErrorMsg(err.message || 'Could not reach this website. Please check the URL.');
      setPageState('error');
    }
  };

  const handleConfirm = async (confirmedData) => {
    setHotelName(confirmedData.hotelName || hotelName);
    setPageState('loading');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const formData = extractedData.formData;
    const payload = {
      url: formData.url,
      email: formData.email,
      hotel_name: confirmedData.hotelName || "",
      city: confirmedData.city || "",
      country: confirmedData.country || "",
      rooms: parseInt(formData.rooms),
      adr: parseFloat(formData.adr),
      currency: formData.currency,
      los: parseFloat(formData.los),
      star_rating: null,
      positioning: "boutique",
      location_type: "city",
      declared_modules: [],
    };

    try {
      const startResp = await fetch(`${API_URL}/api/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!startResp.ok) {
        // Pydantic-layer rejections (e.g. CHAIN_BRAND re-validated inside AuditRequest)
        // arrive with a Pydantic-shape detail array. parseApiError handles both
        // that and the structured HTTPException shape so the right modal still wires up.
        const err = await startResp.json().catch(() => ({}));
        const { errorType: et, errorMsg: em } = parseApiError(err);
        setErrorType(et);
        setErrorMsg(em || 'Failed to start audit.');
        setPageState('error');
        return;
      }

      const { audit_id } = await startResp.json();
      if (!audit_id) throw new Error('No audit ID returned');

      // GTM — audit successfully submitted (backend accepted, audit_id issued).
      // Distinct from `audit_started` (pre-API, could still be rejected) and
      // `audit_completed` (fires ~60s later after full pipeline).
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'audit_submit' });
      } catch (_) {}

      const MAX_POLLS = 100;
      const POLL_INTERVAL = 3000;
      // Tolerate occasional 5xx/network blips, but bail loudly if the backend
      // is consistently rejecting the status poll — otherwise the user waits
      // the full 5-minute timeout for a generic "timed out" message when the
      // real failure was the backend dropping the audit record.
      const POLL_ERROR_THRESHOLD = 5;
      let consecutivePollErrors = 0;
      let lastKnownStatus = 'queued';

      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(res => setTimeout(res, POLL_INTERVAL));

        let pollResp;
        try {
          pollResp = await fetch(`${API_URL}/api/audit/${audit_id}/status`);
        } catch (netErr) {
          // Network-level failure (offline, DNS, CORS). Same threshold treatment
          // as backend 4xx/5xx — a temporary blip is OK, a sustained outage is not.
          consecutivePollErrors++;
          if (consecutivePollErrors >= POLL_ERROR_THRESHOLD) {
            throw new Error(
              `Network error reaching the audit status endpoint (last status: ${lastKnownStatus}). ` +
              `Check your connection and try again.`
            );
          }
          continue;
        }

        if (!pollResp.ok) {
          consecutivePollErrors++;
          // After N consecutive non-OK responses we treat the audit record as
          // genuinely missing/broken on the backend — the user shouldn't sit
          // through the rest of the 5-minute timeout chasing a phantom.
          if (consecutivePollErrors >= POLL_ERROR_THRESHOLD) {
            throw new Error(
              `Audit status endpoint returned ${pollResp.status} repeatedly ` +
              `(last known status: ${lastKnownStatus}). The audit record may have ` +
              `been lost — please start a new audit.`
            );
          }
          continue;
        }
        consecutivePollErrors = 0;

        const data = await pollResp.json();
        if (data.status) lastKnownStatus = data.status;

        if (data.status === 'complete') {
          setReportData(data);
          // Repeat-auditor check — show SOFT_ENGAGEMENT modal once before
          // dropping user on results. They click CTA (Calendly) or Dismiss,
          // then land on results as normal.
          if (data.repeat_auditor && !sessionStorage.getItem('dhi_soft_engagement_shown')) {
            setAuditCount(data.audit_count || 3);
            setShowSoftEngagement(true);
            sessionStorage.setItem('dhi_soft_engagement_shown', '1');
          }
          setPageState('complete');
          // Track audit completion
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'audit_completed',
              audit_id,
              final_score: data.final_score,
              grade: data.grade,
              ai_visibility_score: data.ai_visibility_score,
              duration_s: data.duration_s,
            });
            if (window.posthog?.capture) {
              window.posthog.capture('audit_completed', {
                audit_id,
                final_score: data.final_score,
                grade: data.grade,
                duration_s: data.duration_s,
              });
            }
          } catch (_) {}
          return;
        }

        if (data.status === 'failed') {
          // Pipeline raises ValueError with structured prefixes like
          // "[NO_HOTEL_GBP] ...", "[NON_HOTEL_GBP] ...". Use shared parser
          // so the [TAG] → modal mapping is consistent across all error paths.
          const { errorType: et, errorMsg: em } = parseApiError({ message: data.error });
          setErrorType(et);
          setErrorMsg(em || 'Audit failed on server');
          setPageState('error');
          return;
        }
      }

      // If we exhaust MAX_POLLS without seeing 'complete' or 'failed', surface
      // the last-known status so the backend team can correlate. A stuck
      // 'running' state for 5 minutes is a different signal than no progress
      // ever being reported.
      throw new Error(
        `Audit timed out after 5 minutes (last status: ${lastKnownStatus}).`
      );

    } catch (err) {
      setErrorMsg(err.message);
      setPageState('error');
      // Track audit failure
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'audit_failed',
          error_message: err.message,
        });
        if (window.posthog?.capture) {
          window.posthog.capture('audit_failed', { error: err.message });
        }
      } catch (_) {}
    }
  };

  const handleRetry = () => {
    setPageState('idle');
    setErrorMsg('');
    setErrorType('');
    setReportData(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const renderContent = () => {
    if (pageState === 'extracting') {
      return (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',
          minHeight:'60vh',background:'var(--bg)'}}>
          <div style={{textAlign:'center'}}>
            <div className="spinner" style={{width:32,height:32,margin:'0 auto 20px',borderWidth:3}}></div>
            <p style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:700,
              color:'var(--navy)',marginBottom:8}}>Finding your hotel...</p>
            <p style={{fontSize:13,color:'var(--muted)'}}>
              Reading your website to confirm it's a hotel
            </p>
          </div>
        </div>
      );
    }

    if (pageState === 'confirming') {
      return (
        <HotelConfirmation
          extractedData={extractedData}
          onConfirm={handleConfirm}
          onBack={handleRetry}
          onGbpRejected={({ errorType: et, errorMsg: em }) => {
            setErrorType(et);
            setErrorMsg(em);
            setPageState('error');
          }}
        />
      );
    }

    if (pageState === 'loading') {
      return <LoadingCard hotelName={hotelName} />;
    }

    if (pageState === 'complete') {
      return <ResultsDashboard reportData={reportData} onRunAnother={handleRetry} auditEmail={auditEmail} />;
    }

    // pageState === 'error' falls through to the form — the ErrorModal
    // overlays it. This keeps the user's form context visible behind the
    // modal so they don't feel they've been thrown back to zero.

    return (
      <>
        <HeroSection formRef={formRef} onSubmit={handleFormSubmit} />
        <HowItWorksSection scrollToForm={scrollToForm} />
        <ResultsPreviewSection />
        <DarkStatsSection />
        <TestimonialsSection />
        <FaqSection />
        <CtaStrip headline="Your AI visibility gap is measurable. Your fix plan is ready." sub="Free audit. 60 seconds. No login required." btnLabel="See How AI Sees Your Hotel" scrollToForm={scrollToForm} />
      </>
    );
  };

  // Build the modal context for ErrorModal based on errorType + errorMsg + hotel state.
  // formData lives inside HeroSection (not this scope), so we pull URL from extractedData
  // (which is set whenever /api/extract-hotel succeeds). URL is only used by the
  // URL_UNREACHABLE modal's description, so undefined is fine — modal handles that.
  const modalType = errorTypeToModalType(errorType);
  const modalContext = {
    url: extractedData?.url,
    hotelName: hotelName || extractedData?.hotelName,
    description: errorMsg,
  };

  return (
    <div data-testid="landing-page">
      <Nav />
      <main id="main-content" role="main">
        {renderContent()}
      </main>
      <Footer />

      <ErrorModal
        isOpen={pageState === 'error'}
        type={modalType}
        context={modalContext}
        onRetry={handleRetry}
        onEditUrl={handleRetry}
        onStartOver={handleRetry}
        onDismiss={handleRetry}
        onClose={handleRetry}
      />

      {/* Repeat-auditor engagement modal — fires once per session after
          the 3rd+ completed audit. Dismiss OR book-call both close it
          and drop user on the results dashboard (already mounted via
          pageState === 'complete'). */}
      <ErrorModal
        isOpen={showSoftEngagement}
        type="SOFT_ENGAGEMENT"
        context={{ auditCount }}
        onBookConsultation={() => {
          // ErrorModal handles the Calendly iframe internally; just log.
          try {
            window.posthog?.capture?.('soft_engagement_book_clicked', { audit_count: auditCount });
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'soft_engagement_book_clicked', audit_count: auditCount });
          } catch (_) {}
        }}
        onDismiss={() => setShowSoftEngagement(false)}
        onClose={() => setShowSoftEngagement(false)}
      />
    </div>
  );
};

export default LandingPage;
